<template>
  <div>
    <div class="tw:space-y-1">
      <label v-if="label" class="tw:block" :for="inputId">{{ label }}</label>
      <div class="tw:relative">
        <input
          type="text"
          class="form-control tw:pr-8"
          :id="inputId"
          ref="searchInput"
          v-model="searchQuery"
          :placeholder="placeholder"
          :autofocus="autofocus"
        >
        <Loader2 v-if="isSearching" class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:w-5 tw:h-5 tw:animate-spin tw:text-zinc-500" />
      </div>
    </div>

    <table v-if="users.length > 0" class="table table-striped tw:mt-2">
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>
            <div class="tw:flex tw:items-center tw:gap-3">
              <UserAvatar :user="user" :size="30" />
              <div>
                {{ user.firstname || user.lastname ? `${user.firstname} ${user.lastname}` : user.email }}
                <span v-if="user.status === UserStatusEnum.PENDING_INVITATION" class="badge badge-info">
                  {{ $t('Pending invitation') }}
                </span>
                <br><small>{{ user.company }}</small>
              </div>
            </div>
          </td>
          <td class="align-middle">
            <slot name="action" :user="user">
              <button type="button" class="btn btn-sm btn-outline-primary float-right" @click="onSelect(user)">
                {{ $t('Select') }}
              </button>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <slot v-if="searchQuery.length >= minLength && users.length === 0 && !isSearching" name="empty" :search-query="searchQuery">
      <Empty>
        <template #title>{{ $t('User not found') }}</template>
        <template #description>{{ $t('No user account found for this email.') }}</template>
      </Empty>
    </slot>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { Loader2 } from "@lucide/vue";
import { UserStatusEnum } from "@/types/enums";
import Empty from "@/components/ui/Empty.vue";
import UserAvatar from "@/components/user/UserAvatar.vue";

const props = defineProps({
  searchUrl: { type: String, required: true },
  label: { type: String, default: null },
  placeholder: { type: String, default: "email@site.com" },
  autofocus: { type: Boolean, default: false },
  inputId: { type: String, default: "user-search" },
  minLength: { type: Number, default: 3 },
});

const emit = defineEmits(["select"]);

const searchQuery = ref("");
const users = ref([]);
const isSearching = ref(false);
const searchInput = ref(null);

watch(searchQuery, () => {
  isSearching.value = true;
  searchDebounced();
});

const searchDebounced = useDebounceFn(async () => {
  if (searchQuery.value.length < props.minLength) {
    users.value = [];
    isSearching.value = false;
    return;
  }

  try {
    const { data } = await axios.get(props.searchUrl, {
      params: { q: searchQuery.value },
    });
    users.value = data.data;
  } finally {
    isSearching.value = false;
  }
}, 500);

function onSelect(user) {
  emit("select", user);
}

function reset() {
  searchQuery.value = "";
  users.value = [];
}

function focus() {
  searchInput.value?.focus();
}

defineExpose({ reset, focus, searchQuery });
</script>
